import moment from 'moment';
import React, { Component } from 'react';
import BlockUi from 'react-block-ui';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Col, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalHeader, Row, UncontrolledDropdown } from 'reactstrap';
import { postAPI } from "../../Base/API";
import { CustomHelpPopover } from "../../Base/Common/CommonUIComponents";
import NotificationManager from '../../Base/NotificationCenter/NotificationStore';
import { formatDuration, formatIncidentType, formatUtcDate, incidentTypeIcon, renderSeverityBadge, renderStateBadge } from './ConnectivityIncidentsHelpers';

// Detalhe de um incidente + acoes ack/snooze. O incidente vem do parent (props.incident);
// depois de um acknowledge devolve o incidente actualizado por props.onIncidentUpdated
// para o parent refrescar o modal e a linha da tabela.
class ConnectivityIncidentDetailModal extends Component {

    state = {
        actionBusy: false
    }

    resolveChannelName = (id) => {
        if (!id) return this.props.intl.formatMessage({ id: "connectivity.column.channelNone" });
        const name = this.props.channelNames[String(id)];
        return name || id;
    }

    // Aplica o acknowledge diretamente. snoozeMinutes: null = ate resolver | number = timer em minutos.
    // Sem modal de confirmacao — utilizador pode re-ack a qualquer momento se se enganar.
    applyAcknowledge = (snoozeMinutes) => {
        const { incident } = this.props;
        if (!incident || this.state.actionBusy) return;

        this.setState({ actionBusy: true });

        let url = `/api/Rate/ConnectivityIncident/v1/${incident.id}/acknowledge`;
        if (snoozeMinutes != null) {
            const until = moment().add(snoozeMinutes, 'minutes').utc().toISOString();
            url += `?until=${encodeURIComponent(until)}`;
        }
        postAPI(this.handleActionResult, url, null);
    }

    handleActionResult = (result) => {
        const { data, error } = result;
        if (error) {
            NotificationManager.error(this.props.intl.formatMessage({ id: "connectivity.modal.acknowledgeError" }), '', 3000);
            this.setState({ actionBusy: false });
            return;
        }
        if (data && data.errors && data.errors.length > 0) {
            // Backend devolveu CIC0011/CIC0012/CIC0013 — mostra a mensagem do backend directamente.
            NotificationManager.error(data.errors[0].message, '', 3000);
            this.setState({ actionBusy: false });
            return;
        }
        // Sucesso: o parent substitui o incidente no modal e na lista pelo devolvido pelo endpoint.
        // Se o backend responder 200 mas sem incidente actualizado, avisa que os dados podem estar por refrescar.
        const updated = data && data.responseList && data.responseList[0];
        this.setState({ actionBusy: false });
        if (updated) {
            this.props.onIncidentUpdated(updated);
            NotificationManager.success(this.props.intl.formatMessage({ id: "connectivity.modal.acknowledgeSuccess" }), '', 3000);
        } else {
            NotificationManager.warning(this.props.intl.formatMessage({ id: "connectivity.modal.acknowledgeStale" }), '', 3000);
        }
    }

    detailCell = (labelId, value) => (
        <Col md="6" className="pb-3">
            <div className="text-muted small mb-1"><FormattedMessage id={labelId} /></div>
            <div style={{ wordBreak: 'break-word' }}>{value ?? <span className="text-muted"><FormattedMessage id="connectivity.column.channelNone" /></span>}</div>
        </Col>
    )

    renderDetail = (incident) => {
        if (!incident || !incident.detail) return <span className="text-muted"><FormattedMessage id="connectivity.column.channelNone" /></span>;

        const d = incident.detail;

        if (incident.incidentType === 'ChannelError') {
            const codesList = d.topCodes && d.topCodes.length > 0
                ? d.topCodes.map(c => (
                    <span key={c.code} className="badge badge-light border mr-1">{c.code} × {c.count}</span>
                  ))
                : null;
            const tries = d.servicesTries || {};
            const rooms = d.affectedRoomCategories && d.affectedRoomCategories.length > 0
                ? d.affectedRoomCategories.join(', ')
                : null;
            const firstError = d.firstErrorAt ? formatUtcDate(d.firstErrorAt) : null;
            return (
                <Row>
                    {this.detailCell("connectivity.detail.failedAttempts", d.failedAttempts)}
                    {this.detailCell("connectivity.detail.minutesFailing", d.minutesFailing != null ? `${d.minutesFailing} min` : null)}
                    {this.detailCell("connectivity.detail.firstErrorAt", firstError)}
                    {this.detailCell("connectivity.detail.triesAvail", tries.availability != null ? tries.availability : null)}
                    {this.detailCell("connectivity.detail.triesPrice", tries.price != null ? tries.price : null)}
                    {this.detailCell("connectivity.detail.triesRest", tries.restrictions != null ? tries.restrictions : null)}
                    {this.detailCell("connectivity.detail.rooms", rooms)}
                    <Col md="12" className="pb-2">
                        <div className="text-muted small mb-1"><FormattedMessage id="connectivity.detail.topCodes" /></div>
                        <div>{codesList ?? <span className="text-muted"><FormattedMessage id="connectivity.column.channelNone" /></span>}</div>
                    </Col>
                </Row>
            );
        }
        if (incident.incidentType === 'ChannelSendDelay') {
            const services = d.affectedServices && d.affectedServices.length > 0
                ? d.affectedServices.join(', ')
                : null;
            const rooms = d.affectedRoomCategories && d.affectedRoomCategories.length > 0
                ? d.affectedRoomCategories.join(', ')
                : null;
            const tries = d.servicesTries || {};
            return (
                <Row>
                    {this.detailCell("connectivity.detail.delayMinutes", d.delayMinutes != null ? `${d.delayMinutes} min` : null)}
                    {this.detailCell("connectivity.detail.services", services)}
                    {this.detailCell("connectivity.detail.rooms", rooms)}
                    {this.detailCell("connectivity.detail.triesAvail", tries.availability != null ? tries.availability : null)}
                    {this.detailCell("connectivity.detail.triesPrice", tries.price != null ? tries.price : null)}
                    {this.detailCell("connectivity.detail.triesRest", tries.restrictions != null ? tries.restrictions : null)}
                </Row>
            );
        }
        if (incident.incidentType === 'ReservationPmsDelay') {
            return (
                <Row>
                    {this.detailCell("connectivity.detail.pendingCount", d.pendingCount)}
                    {this.detailCell("connectivity.detail.oldestMinutes", d.oldestMinutes != null ? `${d.oldestMinutes} min` : null)}
                </Row>
            );
        }

        return <pre className="small"><code>{JSON.stringify(d, null, 2)}</code></pre>;
    }

    // Bloco no topo do modal (modelo ack+snooze unificado):
    // - Info a esquerda: estado atual (nenhum / ate resolver / silenciado / expirado)
    // - Botao a direita "Marcar como visto" (so se State != Resolved)
    // - Se State == Resolved e sem ack, bloco nao aparece.
    renderActionsBlock = (inc) => {
        const nowUtc = moment.utc();
        const acked = inc.acknowledgedAt != null;
        const snoozeActive = inc.snoozedUntil && moment.utc(inc.snoozedUntil).isAfter(nowUtc);
        const snoozeExpired = inc.snoozedUntil && !snoozeActive;
        const untilResolved = acked && !inc.snoozedUntil;
        const canAct = inc.state !== 'Resolved';
        if (!acked && !canAct) return null;

        const userLabel = inc.acknowledgedByName || inc.acknowledgedBy || '';
        return (
            <div className="mb-4 p-3" style={{ backgroundColor: '#f5f7fa', borderRadius: '4px', borderLeft: '3px solid #0665ff', position: 'relative' }}>
                <div className="text-right">
                    <CustomHelpPopover
                        helperTarget={`connectivity-actions-help-${inc.id}`}
                        placement="left"
                        help={<FormattedMessage id="connectivity.modal.actionsHelp" />}
                    />
                </div>
                <Row className="align-items-center">
                    <Col>
                        {!acked && canAct && (
                            <div className="small text-muted">
                                <FormattedMessage id="connectivity.modal.noActionsYet" />
                            </div>
                        )}
                        {untilResolved && (
                            <div className="small mb-1">
                                <i className="fas fa-check-circle text-success mr-2"></i>
                                <FormattedMessage
                                    id="connectivity.modal.acknowledgedUntilResolved"
                                    values={{ date: formatUtcDate(inc.acknowledgedAt), user: userLabel }}
                                />
                            </div>
                        )}
                        {acked && snoozeActive && (
                            <div className="small mb-1">
                                <i className="fas fa-bell-slash text-secondary mr-2"></i>
                                <FormattedMessage
                                    id="connectivity.modal.acknowledgedSnoozed"
                                    values={{ date: formatUtcDate(inc.acknowledgedAt), user: userLabel, until: formatUtcDate(inc.snoozedUntil) }}
                                />
                            </div>
                        )}
                        {acked && snoozeExpired && (
                            <div className="small mb-1 text-muted">
                                <i className="fas fa-bell-slash mr-2"></i>
                                <FormattedMessage
                                    id="connectivity.modal.acknowledgedExpired"
                                    values={{ date: formatUtcDate(inc.acknowledgedAt), user: userLabel, expired: formatUtcDate(inc.snoozedUntil) }}
                                />
                            </div>
                        )}
                    </Col>
                    {canAct && !snoozeActive && (
                        <Col md="auto" className="text-right">
                            <UncontrolledDropdown>
                                <DropdownToggle color="host" size="sm" caret disabled={this.state.actionBusy}>
                                    <FormattedMessage id="connectivity.modal.acknowledge" />
                                </DropdownToggle>
                                <DropdownMenu right>
                                    <DropdownItem onClick={() => this.applyAcknowledge(60)}>
                                        <FormattedMessage id="connectivity.modal.snooze.1hour" />
                                    </DropdownItem>
                                    <DropdownItem onClick={() => this.applyAcknowledge(240)}>
                                        <FormattedMessage id="connectivity.modal.snooze.4hours" />
                                    </DropdownItem>
                                    <DropdownItem onClick={() => this.applyAcknowledge(1440)}>
                                        <FormattedMessage id="connectivity.modal.snooze.1day" />
                                    </DropdownItem>
                                    <DropdownItem onClick={() => this.applyAcknowledge(10080)}>
                                        <FormattedMessage id="connectivity.modal.snooze.1week" />
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem onClick={() => this.applyAcknowledge(null)}>
                                        <FormattedMessage id="connectivity.modal.snooze.untilResolved" />
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </Col>
                    )}
                </Row>
            </div>
        );
    }

    render() {
        const { intl, incident, modal, toggle } = this.props;

        return (
            <Modal isOpen={modal} toggle={toggle}>
                <ModalHeader toggle={toggle}>
                    <FormattedMessage id="connectivity.modal.title" />
                </ModalHeader>
                <ModalBody>
                    <BlockUi tag="div" blocking={this.state.actionBusy}>
                    {incident && (
                        <div>
                            <div className="mb-4 d-flex align-items-center justify-content-between">
                                <div className="flex-grow-1 mr-3" style={{ minWidth: 0 }}>
                                    <div className="text-truncate" style={{ fontWeight: 500, fontSize: '1.05rem' }}>
                                        {this.resolveChannelName(incident.channelInstanceId)}
                                        {incident.channelInstanceId && <small className="text-muted ml-2">#{incident.channelInstanceId}</small>}
                                    </div>
                                </div>
                                <div className="text-right d-flex align-items-center" style={{ flexShrink: 0, gap: '0.5rem' }}>
                                    {renderSeverityBadge(intl, incident.severity)}
                                    {renderStateBadge(intl, incident.state)}
                                </div>
                            </div>

                            {this.renderActionsBlock(incident)}

                            <Row className="mb-4">
                                <Col md="4">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}><FormattedMessage id="connectivity.column.type" /></div>
                                    <div>
                                        <i className={`${incidentTypeIcon(incident.incidentType)} mr-2 text-muted`}></i>
                                        {formatIncidentType(intl, incident.incidentType)}
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}><FormattedMessage id="connectivity.column.duration" /></div>
                                    <div>{formatDuration(incident)}</div>
                                </Col>
                                <Col md="4">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}><FormattedMessage id="connectivity.column.startedAt" /></div>
                                    <div>{formatUtcDate(incident.startedAt)}</div>
                                </Col>
                                <Col md="4" className="mt-3">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}><FormattedMessage id="connectivity.column.lastDetectedAt" /></div>
                                    <div>{formatUtcDate(incident.lastDetectedAt)}</div>
                                </Col>
                                {incident.resolvedAt && (
                                    <Col md="4" className="mt-3">
                                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}><FormattedMessage id="connectivity.column.resolvedAt" /></div>
                                        <div>{formatUtcDate(incident.resolvedAt)}</div>
                                    </Col>
                                )}
                            </Row>

                            <div className="text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                <FormattedMessage id="connectivity.modal.detail" />
                            </div>
                            <div className="p-3" style={{ backgroundColor: '#fafbfc', borderRadius: '4px', borderLeft: '3px solid #dee2e6' }}>
                                {this.renderDetail(incident)}
                            </div>
                            {incident.detail && (
                                <details className="mt-3">
                                    <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                                        <FormattedMessage id="connectivity.modal.rawJson" />
                                    </summary>
                                    <pre className="mt-2 p-2 small mb-0" style={{ backgroundColor: '#f5f7fa', borderRadius: '4px' }}>
                                        <code>{JSON.stringify(incident.detail, null, 2)}</code>
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}
                    </BlockUi>
                </ModalBody>
            </Modal>
        );
    }
}

export default injectIntl(ConnectivityIncidentDetailModal);
